import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Switch,
  message,
  Typography,
  Space,
  Tag,
  Row,
  Col,
  Popconfirm
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import { notificationAPI, stationOwnerAPI } from '../services/api';

const { Title } = Typography;
const { Option } = Select;

interface Alert {
  _id: string;
  station?: {
    _id: string;
    name: string;
  };
  alertType: string;
  category: string;
  conditions: any;
  notificationSettings: any;
  isActive: boolean;
  lastTriggered?: string;
  triggerCount: number;
}

const Alerts: React.FC = () => {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [stations, setStations] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAlert, setEditingAlert] = useState<Alert | null>(null);
  const [form] = Form.useForm();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    fetchStations();
    fetchAlerts();
  }, []);

  const fetchStations = async () => {
    try {
      const response = await stationOwnerAPI.getStations();
      setStations(response);
    } catch (error) {
      message.error('İstasyonlar yüklenemedi');
    }
  };

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const response = await notificationAPI.getAlerts();
      setAlerts(response);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Alert\'ler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAlert = () => {
    setEditingAlert(null);
    form.resetFields();
    form.setFieldsValue({
      isActive: true,
      notificationSettings: {
        inApp: { enabled: true },
        email: { enabled: false },
        sms: { enabled: false }
      }
    });
    setModalVisible(true);
  };

  const handleEditAlert = (alert: Alert) => {
    setEditingAlert(alert);
    form.setFieldsValue({
      stationId: alert.station?._id || null,
      alertType: alert.alertType,
      category: alert.category,
      ...alert.conditions,
      ...alert.notificationSettings,
      isActive: alert.isActive
    });
    setModalVisible(true);
  };

  const handleSubmit = async (values: any) => {
    try {
      const data = {
        stationId: values.stationId || null,
        alertType: values.alertType,
        category: values.category,
        conditions: {
          fuelType: values.fuelType,
          operator: values.operator,
          threshold: values.threshold,
          changePercent: values.changePercent,
          productType: values.productType,
          productName: values.productName,
          minStockLevel: values.minStockLevel,
          targetAmount: values.targetAmount,
          period: values.period
        },
        notificationSettings: {
          email: {
            enabled: values.emailEnabled || false,
            recipients: values.emailRecipients ? values.emailRecipients.split(',') : []
          },
          sms: {
            enabled: values.smsEnabled || false,
            phone: values.smsPhone
          },
          inApp: {
            enabled: values.inAppEnabled !== undefined ? values.inAppEnabled : true
          }
        },
        priority: values.priority || 'normal',
        isActive: values.isActive !== undefined ? values.isActive : true
      };

      if (editingAlert) {
        await notificationAPI.updateAlert(editingAlert._id, data);
        message.success('Alert güncellendi');
      } else {
        await notificationAPI.createAlert(data);
        message.success('Alert oluşturuldu');
      }

      setModalVisible(false);
      form.resetFields();
      setEditingAlert(null);
      fetchAlerts();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'İşlem başarısız');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await notificationAPI.deleteAlert(id);
      message.success('Alert silindi');
      fetchAlerts();
    } catch (error: any) {
      message.error('İşlem başarısız');
    }
  };

  const handleCheckAlerts = async () => {
    try {
      const response = await notificationAPI.checkAlerts();
      message.success(`${response.triggeredCount} alert tetiklendi`);
      fetchAlerts();
    } catch (error: any) {
      message.error('Alert kontrolü başarısız');
    }
  };

  const columns = [
    {
      title: 'Tip',
      dataIndex: 'alertType',
      key: 'alertType',
      render: (type: string) => {
        const labels: any = {
          price_threshold: 'Fiyat Eşiği',
          stock_low: 'Düşük Stok',
          revenue_target: 'Gelir Hedefi',
          custom: 'Özel'
        };
        return <Tag color="blue">{labels[type] || type}</Tag>;
      }
    },
    {
      title: 'Kategori',
      dataIndex: 'category',
      key: 'category',
      render: (category: string) => {
        const labels: any = {
          fuel_price: 'Yakıt Fiyatı',
          ev_price: 'EV Şarj Fiyatı',
          stock: 'Stok',
          revenue: 'Gelir',
          transaction: 'İşlem',
          other: 'Diğer'
        };
        return <Tag>{labels[category] || category}</Tag>;
      }
    },
    {
      title: 'İstasyon',
      dataIndex: ['station', 'name'],
      key: 'station',
      render: (name: string) => name || 'Tüm İstasyonlar'
    },
    {
      title: 'Durum',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'success' : 'default'}>
          {isActive ? 'Aktif' : 'Pasif'}
        </Tag>
      )
    },
    {
      title: 'Tetiklenme',
      key: 'trigger',
      render: (record: Alert) => (
        <div>
          <div>Sayı: {record.triggerCount}</div>
          {record.lastTriggered && (
            <div style={{ fontSize: '12px', color: '#999' }}>
              {new Date(record.lastTriggered).toLocaleDateString('tr-TR')}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 150,
      render: (_: any, record: Alert) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditAlert(record)}
          />
          <Popconfirm
            title="Bu alert'i silmek istediğinizden emin misiniz?"
            onConfirm={() => handleDelete(record._id)}
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} md={12}>
          <Title level={2} style={{ margin: 0, fontSize: isMobile ? '20px' : '24px' }}>
            <ThunderboltOutlined /> Otomatik Uyarılar
          </Title>
        </Col>
        <Col xs={24} md={12}>
          {isMobile ? (
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              <Button onClick={handleCheckAlerts} block>
                Alert'leri Kontrol Et
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAddAlert} block>
                Yeni Alert Oluştur
              </Button>
            </Space>
          ) : (
            <Space size="small">
              <Button onClick={handleCheckAlerts}>
                Alert'leri Kontrol Et
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAddAlert}>
                Yeni Alert Oluştur
              </Button>
            </Space>
          )}
        </Col>
      </Row>

      <Card styles={{ body: { padding: window.innerWidth < 768 ? '8px' : '24px' } }}>
        <Table
          columns={columns}
          dataSource={alerts}
          rowKey="_id"
          loading={loading}
          pagination={{ 
            pageSize: 10,
            showSizeChanger: false,
            responsive: true
          }}
          scroll={{ x: 'max-content', y: window.innerWidth < 768 ? '60vh' : undefined }}
          size={window.innerWidth < 768 ? 'small' : 'middle'}
        />
      </Card>

      <Modal
        title={editingAlert ? 'Alert Düzenle' : 'Yeni Alert Oluştur'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingAlert(null);
          form.resetFields();
        }}
        footer={null}
        width={isMobile ? '95%' : 800}
        styles={{ body: { padding: isMobile ? '16px' : '24px', maxHeight: '80vh', overflowY: 'auto' } }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="alertType"
                label="Alert Tipi"
                rules={[{ required: true, message: 'Alert tipi seçiniz' }]}
              >
                <Select placeholder="Alert tipi">
                  <Option value="price_threshold">Fiyat Eşiği</Option>
                  <Option value="stock_low">Düşük Stok</Option>
                  <Option value="revenue_target">Gelir Hedefi</Option>
                  <Option value="custom">Özel</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="category"
                label="Kategori"
                rules={[{ required: true, message: 'Kategori seçiniz' }]}
              >
                <Select placeholder="Kategori">
                  <Option value="fuel_price">Yakıt Fiyatı</Option>
                  <Option value="ev_price">EV Şarj Fiyatı</Option>
                  <Option value="stock">Stok</Option>
                  <Option value="revenue">Gelir</Option>
                  <Option value="transaction">İşlem</Option>
                  <Option value="other">Diğer</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="stationId"
            label="İstasyon (Boş bırakılırsa tüm istasyonlar)"
          >
            <Select placeholder="İstasyon seçin (opsiyonel)" allowClear>
              {stations.map((station: any) => (
                <Option key={station._id} value={station._id}>
                  {station.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.alertType !== currentValues.alertType}>
            {({ getFieldValue }) => {
              const alertType = getFieldValue('alertType');
              
              if (alertType === 'price_threshold') {
                return (
                  <>
                    <Row gutter={[16, 16]}>
                      <Col xs={24} sm={12}>
                        <Form.Item name="fuelType" label="Yakıt Tipi">
                          <Select>
                            <Option value="benzin">Benzin</Option>
                            <Option value="diesel">Dizel</Option>
                            <Option value="lpg">LPG</Option>
                            <Option value="premium_benzin">Premium Benzin</Option>
                            <Option value="premium_diesel">Premium Dizel</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item name="operator" label="Operatör">
                          <Select>
                            <Option value="above">Üzerinde</Option>
                            <Option value="below">Altında</Option>
                            <Option value="equals">Eşit</Option>
                            <Option value="change_percent">Değişim Yüzdesi</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                      <Col xs={24} sm={12}>
                        <Form.Item name="threshold" label="Eşik Değeri (₺)">
                          <InputNumber style={{ width: '100%' }} min={0} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item name="changePercent" label="Değişim Yüzdesi (%)">
                          <InputNumber style={{ width: '100%' }} min={0} max={100} />
                        </Form.Item>
                      </Col>
                    </Row>
                  </>
                );
              }
              
              if (alertType === 'stock_low') {
                return (
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12}>
                      <Form.Item name="productType" label="Ürün Tipi">
                        <Select>
                          <Option value="fuel">Yakıt</Option>
                          <Option value="market">Market</Option>
                          <Option value="car_wash">Oto Yıkama</Option>
                          <Option value="restaurant">Restoran</Option>
                          <Option value="other">Diğer</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item name="minStockLevel" label="Minimum Stok Seviyesi">
                        <InputNumber style={{ width: '100%' }} min={0} />
                      </Form.Item>
                    </Col>
                  </Row>
                );
              }
              
              return null;
            }}
          </Form.Item>

          <Title level={4}>Bildirim Ayarları</Title>
          
          <Form.Item name="inAppEnabled" valuePropName="checked" label="Uygulama İçi Bildirim">
            <Switch />
          </Form.Item>
          
          <Form.Item name="emailEnabled" valuePropName="checked" label="E-posta Bildirimi">
            <Switch />
          </Form.Item>
          
          <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.emailEnabled !== currentValues.emailEnabled}>
            {({ getFieldValue }) => 
              getFieldValue('emailEnabled') && (
                <Form.Item name="emailRecipients" label="E-posta Adresleri (virgülle ayırın)">
                  <Input placeholder="email1@example.com, email2@example.com" />
                </Form.Item>
              )
            }
          </Form.Item>
          
          <Form.Item name="smsEnabled" valuePropName="checked" label="SMS Bildirimi">
            <Switch />
          </Form.Item>
          
          <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.smsEnabled !== currentValues.smsEnabled}>
            {({ getFieldValue }) => 
              getFieldValue('smsEnabled') && (
                <Form.Item name="smsPhone" label="Telefon Numarası">
                  <Input placeholder="05551234567" />
                </Form.Item>
              )
            }
          </Form.Item>

          <Form.Item name="isActive" valuePropName="checked" label="Aktif">
            <Switch />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingAlert ? 'Güncelle' : 'Oluştur'}
              </Button>
              <Button onClick={() => {
                setModalVisible(false);
                setEditingAlert(null);
                form.resetFields();
              }}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Alerts;

